import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

export const useTutorialStore = create(
  persist(
    (set, get) => ({
      isActive: false,
      currentStep: 0,
      completedSteps: [],
      shouldShow: null, // null = not determined, true = show, false = don't show
      
      // Check if tutorial should be shown based on user's onboarding status
      checkShouldShow: (user) => {
        if (!user) {
          set({ shouldShow: false });
          return false;
        }
        
        const onboarding = user.onboarding || {};
        const shouldShow = !onboarding.completed && !onboarding.skipped;
        
        set({ 
          shouldShow,
          currentStep: onboarding.currentStep || 0,
          completedSteps: onboarding.completedSteps?.map(s => s.step) || []
        });
        
        return shouldShow;
      },
      
      // Start the tutorial
      startTutorial: () => {
        set({ 
          isActive: true, 
          currentStep: 0 
        });
      },
      
      // Move to next step
      nextStep: async () => {
        const { currentStep } = get();
        const newStep = currentStep + 1;
        
        set({ currentStep: newStep });
        
        // Update backend
        try {
          await api.patch('/users/me/onboarding', {
            currentStep: newStep
          });
        } catch (error) {
          console.error('Failed to update tutorial step:', error);
        }
      },
      
      // Complete a specific step
      completeStep: async (stepName) => {
        const { completedSteps } = get();
        
        if (!completedSteps.includes(stepName)) {
          const newCompletedSteps = [...completedSteps, stepName];
          set({ completedSteps: newCompletedSteps });
          
          // Update backend
          try {
            await api.patch('/users/me/onboarding', {
              completedSteps: [stepName]
            });
          } catch (error) {
            console.error('Failed to update completed step:', error);
          }
        }
      },
      
      // Complete the entire tutorial
      completeTutorial: async () => {
        set({ 
          isActive: false,
          shouldShow: false,
          currentStep: 0 // Reset to beginning for next time (if they want to see it again)
        });
        
        try {
          await api.patch('/users/me/onboarding', {
            completed: true,
            currentStep: 0 // Reset current step when completed
          });
        } catch (error) {
          console.error('Failed to complete tutorial:', error);
        }
      },
      
      // Skip the tutorial
      skipTutorial: async () => {
        set({ 
          isActive: false,
          shouldShow: false,
          currentStep: 0 // Reset to beginning
        });
        
        try {
          await api.patch('/users/me/onboarding', {
            skipped: true,
            currentStep: 0 // Reset current step when skipped
          });
        } catch (error) {
          console.error('Failed to skip tutorial:', error);
        }
      },
      
      // Reset tutorial (for testing or if user wants to see it again)
      resetTutorial: async () => {
        set({
          isActive: false,
          currentStep: 0,
          completedSteps: [],
          shouldShow: true
        });
        
        try {
          await api.patch('/users/me/onboarding', {
            completed: false,
            skipped: false,
            currentStep: 0
          });
        } catch (error) {
          console.error('Failed to reset tutorial:', error);
        }
      }
    }),
    {
      name: 'neoshift-tutorial',
      partialize: (state) => ({
        currentStep: state.currentStep,
        completedSteps: state.completedSteps
      })
    }
  )
);
